import React, { FC, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import { useTranslation } from 'react-i18next';

import { ILabel } from '@store/modules/boxes/types';
import BoxesService from '@services/boxes';
import { displayErrorNotification } from '@utils/utils';
import { DEFAULT_LABEL_COLOR, getReadableTextColor } from '@utils/colors';

import styles from './EnvelopeLabels.module.scss';

interface IEnvelopeLabelsProps {
  labels?: ILabel[] | null;
  availableLabels?: ILabel[];
  className?: string;
  editable?: boolean;
  boxOguid?: string;
  envelopeOguid?: string;
  onChange?: () => void;
  onLabelsChange?: (labels: ILabel[]) => void;
}

const EnvelopeLabels: FC<IEnvelopeLabelsProps> = ({
  labels = [],
  availableLabels: providedAvailableLabels,
  className,
  editable = false,
  boxOguid,
  envelopeOguid,
  onChange,
  onLabelsChange,
}) => {
  const { t } = useTranslation(['envelope', 'common']);
  const [loadedAvailableLabels, setLoadedAvailableLabels] = useState<ILabel[]>([]);
  const [displayLabels, setDisplayLabels] = useState<ILabel[]>(labels ?? []);
  const [updating, setUpdating] = useState(false);
  const availableLabels = providedAvailableLabels ?? loadedAvailableLabels;

  useEffect(() => {
    if (providedAvailableLabels !== undefined) return;

    if (!editable || !boxOguid) return;

    BoxesService.getLabels(boxOguid)
      .then((response) => setLoadedAvailableLabels(response.data))
      .catch(displayErrorNotification);
  }, [boxOguid, editable, providedAvailableLabels]);

  useEffect(() => setDisplayLabels(labels ?? []), [labels]);

  const orderedDisplayLabels = useMemo(() => {
    if (!availableLabels.length) return displayLabels;

    const labelOrder = new Map(
      availableLabels.map(({ fullKey }, index) => [fullKey, index])
    );

    return displayLabels
      .map((label, index) => ({ index, label, order: labelOrder.get(label.fullKey) }))
      .sort((first, second) => {
        if (first.order === undefined && second.order === undefined) {
          return first.index - second.index;
        }
        if (first.order === undefined) return 1;
        if (second.order === undefined) return -1;

        return first.order - second.order;
      })
      .map(({ label }) => label);
  }, [availableLabels, displayLabels]);

  const handleAssign = (evt: React.ChangeEvent<HTMLSelectElement>): void => {
    if (updating) return;

    const key = evt.currentTarget.value;
    const addedLabel = availableLabels.find(({ fullKey }) => fullKey === key);
    if (!addedLabel) return;

    const nextLabels = addedLabel.group
      ? displayLabels.filter(({ group }) => group?.key !== addedLabel.group?.key)
      : [...displayLabels];
    if (!nextLabels.some(({ fullKey }) => fullKey === addedLabel.fullKey)) nextLabels.push(addedLabel);

    if (onLabelsChange) {
      setDisplayLabels(nextLabels);
      onLabelsChange(nextLabels);
      return;
    }

    if (!boxOguid || !envelopeOguid) return;

    setUpdating(true);
    BoxesService.assignEnvelopeLabel(boxOguid, key, [envelopeOguid])
      .then(() => {
        setDisplayLabels(nextLabels);
        onChange?.();
      })
      .catch(displayErrorNotification)
      .finally(() => setUpdating(false));
  };

  const handleRemove = (fullKey: string): void => {
    if (updating) return;

    const nextLabels = displayLabels.filter((label) => label.fullKey !== fullKey);

    if (onLabelsChange) {
      setDisplayLabels(nextLabels);
      onLabelsChange(nextLabels);
      return;
    }

    if (!boxOguid || !envelopeOguid) return;

    setUpdating(true);
    BoxesService.removeEnvelopeLabel(boxOguid, fullKey, [envelopeOguid])
      .then(() => {
        setDisplayLabels(nextLabels);
        onChange?.();
      })
      .catch(displayErrorNotification)
      .finally(() => setUpdating(false));
  };

  if (!displayLabels.length && (!editable || !availableLabels.length)) return null;

  return (
    <span className={cn(styles.labels, className)}>
      {orderedDisplayLabels.map((label) => {
        const backgroundColor = label.color ?? DEFAULT_LABEL_COLOR;

        return (
          <span
            className={styles.label}
            key={label.fullKey}
            style={{ backgroundColor, color: getReadableTextColor(backgroundColor) }}
            title={label.group ? `${label.group.name} / ${label.name}` : label.name}
          >
            {label.name}
            {editable && (
              <button
                aria-label={`${t('common:delete')} ${label.name}`}
                className={styles.remove}
                disabled={updating}
                onClick={() => handleRemove(label.fullKey)}
                type='button'
              >
                ×
              </button>
            )}
          </span>
        );
      })}
      {editable && Boolean(availableLabels.length) && (
        <select
          aria-label='Envelope labels'
          className={styles.editor}
          disabled={updating}
          onChange={handleAssign}
          value=''
        >
          <option value=''>{`+ ${t('envelope:labels')}`}</option>
          {availableLabels.map((label) => (
            <option
              disabled={displayLabels.some(({ fullKey }) => fullKey === label.fullKey)}
              key={label.fullKey}
              value={label.fullKey}
            >
              {label.group ? `${label.group.name} / ${label.name}` : label.name}
            </option>
          ))}
        </select>
      )}
    </span>
  );
};

export default EnvelopeLabels;
